import React from 'react';
import PropTypes from 'prop-types';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import { EyeIconSVG } from 'utils/Svg';
import { ACL } from 'components/common';

const RequestCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
	transition: box-shadow 0.3s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

const CardBody = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
`;

const InfoColumns = styled(Box)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px 40px;
	flex: 1;
`;

const InfoField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	line-height: 1.4;
	width: 140px;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	line-height: 1.5;
`;

const ActionSection = styled(Flex)`
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
	align-self: center;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 4px;
	border: none;
	cursor: pointer;
	color: #000;
	font-size: 16px;
	transition: all 0.2s ease;
	margin-right: 15px;
	padding: 0;

	span {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px !important;
		height: 20px !important;
		margin-right: 0 !important;
	}

	svg {
		width: 20px !important;
		height: 20px !important;
		max-width: 20px;
		max-height: 20px;
	}

	&:hover {
		background: #bae7ff;
	}

	&:active {
		background: #91d5ff;
	}
`;

const ApproveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	padding: 8px 18px;
	min-width: 130px;

	background: #fff;
	color: #52c41a;

	border: 1.5px solid #52c41a;
	border-radius: 6px;

	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	transition: all 0.25s ease;

	.anticon {
		font-size: 16px;
		color: #52c41a;
	}

	&:hover {
		background: #f6ffed;
		border-color: #73d13d;
		color: #73d13d;
	}

	&:active {
		background: #d9f7be;
		border-color: #52c41a;
	}
`;

const RejectButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 20px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	min-width: 130px;

	.anticon {
		font-size: 16px;
	}

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
		transform: translateY(-1px);
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
		transform: translateY(0);
	}
`;

const truncateAddress = (address, maxLength = 30) => {
	if (!address) return '-';
	if (address.length <= maxLength) return address;
	return `${address.substring(0, maxLength)}...`;
};

export default function VendorContractCardV2({ contract, onView, onApprove, onReject, canDecide }) {
	return (
		<RequestCard>
			<CardBody>
				<InfoColumns>
					<InfoField>
						<FieldLabel>Vendor Name :</FieldLabel>
						<FieldValue>
							<strong>{contract.vendor_name || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Vendor Type :</FieldLabel>
						<FieldValue>
							<strong>{contract.vendor_type || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Legal Structure :</FieldLabel>
						<FieldValue>
							<strong>{contract.legal_structure || '-'}</strong>
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>PoC Name :</FieldLabel>
						<FieldValue>{contract.poc_name || '-'}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>PoC Number :</FieldLabel>
						<FieldValue>{contract.poc_number || '-'}</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Address :</FieldLabel>
						<FieldValue title={contract.address || '-'}>
							{truncateAddress(contract.address)}
						</FieldValue>
					</InfoField>

					<InfoField>
						<FieldLabel>Created by :</FieldLabel>
						<FieldValue>{contract.created_by || '-'}</FieldValue>
					</InfoField>
				</InfoColumns>
				<ActionSection>
					<ViewButton onClick={() => onView(contract.id)}>
						<EyeIconSVG />
					</ViewButton>
					{canDecide && (
						<>
							<ACL action="update" module="handle_vendor_contract_approval">
								<ApproveButton onClick={() => onApprove(contract.id)}>
									<CheckOutlined />
									Approve
								</ApproveButton>
							</ACL>
							<ACL action="update" module="handle_vendor_contract_approval">
								<RejectButton onClick={() => onReject(contract.id)}>
									<CloseOutlined />
									Reject
								</RejectButton>
							</ACL>
						</>
					)}
				</ActionSection>
			</CardBody>
		</RequestCard>
	);
}

VendorContractCardV2.propTypes = {
	contract: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		vendor_name: PropTypes.string,
		vendor_type: PropTypes.string,
		legal_structure: PropTypes.string,
		poc_name: PropTypes.string,
		poc_number: PropTypes.string,
		address: PropTypes.string,
		created_by: PropTypes.string,
	}).isRequired,
	onView: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
	canDecide: PropTypes.bool,
};

VendorContractCardV2.defaultProps = {
	canDecide: true,
};
